import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { COUNTRY } from '../constant/country';
import { getPublishedApps } from '../lib/manuals';
import { addException, deleteException, getExceptions } from '../lib/visibility';
import { addNotice, deleteNotice, getAllNotices } from '../lib/notice';
import EifuHeader from './EifuHeader';

// Admin console (route: /admin) for the eIFU country-visibility EXCEPTIONS
// (SPEC-002). Default = everything visible everywhere. Operators add exceptions:
// for a set of countries, either hide a whole SW or restrict it to specific
// document versions. Language is not a visibility axis here.
//
// Access to /admin (and the write API under /admin) is gated by Cloudflare Access
// (Entra SSO); the Pages Function verifies the assertion independently.
const EMPTY_FORM = { countries: [], appName: '', mode: 'hide', versions: [] };

// Portal languages (i18n runtime codes) for authoring a multilingual notice.
const NOTICE_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' },
  { code: 'jp', label: '日本語' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ru', label: 'Русский' },
  { code: 'zhchs', label: '简体中文' },
  { code: 'zhcht', label: '繁體中文' },
];
const EMPTY_NOTICE = { content: {}, lang: 'en' };

// Notice display-period presets (days) and default. `null` days = no expiry.
const EXPIRY_PRESETS = [7, 14, 30, 90];
const DEFAULT_EXPIRY_DAYS = 30;

// Parse a stored UTC 'YYYY-MM-DD HH:MM:SS' expiry into a Date (or null).
const parseUtc = (s) => (s ? new Date(`${s.replace(' ', 'T')}Z`) : null);

// Local YYYY-MM-DD for display.
const fmtDate = (d) => {
  if (!d || Number.isNaN(d.getTime())) {
    return '';
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isNoticeExpired = (n) => {
  const d = parseUtc(n.expiresAt);
  return d ? d.getTime() <= Date.now() : false;
};

// Compact, tidy styling scoped to the admin page only (the public portal keeps
// the larger marketing theme). Overrides the theme's oversized fonts/controls.
const ADMIN_STYLE = `
.eifu-admin-page { color: #3b4252; font-size: 0.78rem; }
.eifu-admin-page .admin-title { font-size: 0.98rem; font-weight: 700; margin-bottom: 0.25rem; }
.eifu-admin-page .admin-section { font-size: 0.8rem; font-weight: 600; margin-bottom: 0.5rem; }
.eifu-admin-page .alert { padding: 0.5rem 0.7rem; font-size: 0.74rem; margin-bottom: 0.9rem; }
.eifu-admin-page .form-label { font-size: 0.68rem; color: #6b7280; margin-bottom: 0.15rem; }
.eifu-admin-page .admin-card { background: #fafafe; border: 1px solid #ececf3; border-radius: 0.5rem; padding: 0.85rem 0.95rem; margin-bottom: 1.25rem; }
.eifu-admin-page .ray-select__control { min-height: 30px; font-size: 0.78rem; border-radius: 0.35rem; }
.eifu-admin-page .ray-select__value-container { padding: 0 6px; }
.eifu-admin-page .ray-select__indicators { height: 28px; }
.eifu-admin-page .ray-select__dropdown-indicator, .eifu-admin-page .ray-select__clear-indicator { padding: 4px; }
.eifu-admin-page .ray-select__placeholder { font-size: 0.78rem; }
.eifu-admin-page .ray-select__multi-value { font-size: 0.72rem; }
.eifu-admin-page .ray-select__menu { font-size: 0.78rem; }
.eifu-admin-page .btn { font-size: 0.72rem; padding: 0.28rem 0.6rem; border-radius: 0.35rem; }
.eifu-admin-page .btn-sm { font-size: 0.68rem; padding: 0.2rem 0.5rem; }
.eifu-admin-page table { font-size: 0.76rem; }
.eifu-admin-page table thead th { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: #8a90a2; font-weight: 600; }
.eifu-admin-page table td, .eifu-admin-page table th { padding: 0.4rem 0.5rem; vertical-align: middle; }
.eifu-admin-page .badge { font-weight: 500; font-size: 0.64rem; }
.eifu-admin-page input[type="radio"] { transform: scale(0.9); }
.eifu-admin-page .admin-tabs { display: flex; gap: 0.35rem; border-bottom: 1px solid #ececf3; margin-bottom: 1.1rem; }
.eifu-admin-page .admin-tabs button { appearance: none; background: transparent; border: none; border-bottom: 2px solid transparent; color: #8a90a2; font-size: 0.82rem; font-weight: 600; padding: 0.4rem 0.7rem; cursor: pointer; }
.eifu-admin-page .admin-tabs button.active { color: #6b4dff; border-bottom-color: #8574FF; }
.eifu-admin-page .expiry-hint { font-size: 0.68rem; color: #8a90a2; }
.eifu-admin-page .expiry-days { width: 74px; display: inline-block; }
`;

const EifuAdmin = () => {
  const { i18n } = useTranslation(['translation']);
  // The admin console supports Korean and English only. Follow the top-right
  // language selection when it is Korean; for English or any other language
  // (jp/de/ru/zh-*), render in English.
  const t = i18n.language === 'ko' ? i18n.getFixedT('ko') : i18n.getFixedT('en');
  // Which admin section is shown. Two tabs: country exceptions and notices.
  const [tab, setTab] = useState('exceptions');
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notices, setNotices] = useState([]);
  const [notice, setNotice] = useState(EMPTY_NOTICE);
  // Requested display period for a new notice: positive day-count, or null = no expiry.
  const [expiryDays, setExpiryDays] = useState(DEFAULT_EXPIRY_DAYS);
  const [noticeSaving, setNoticeSaving] = useState(false);
  const [noticeError, setNoticeError] = useState('');

  const refresh = useCallback(() => getExceptions().then(setExceptions), []);

  useEffect(() => {
    Promise.all([getPublishedApps(), getExceptions(), getAllNotices()])
      .then(([appList, exList, noticeList]) => {
        setApps(_.orderBy(appList || [], ['title'], ['asc']));
        setExceptions(Array.isArray(exList) ? exList : []);
        setNotices(Array.isArray(noticeList) ? noticeList : []);
      })
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  }, []);

  const countryOptions = useMemo(
    () =>
      COUNTRY.map((c) => ({ value: c.code, label: t(c.label || c.code) })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const appOptions = useMemo(
    () => apps.map((a) => ({ value: a.name, label: a.title || a.name })),
    [apps]
  );

  const appByName = useMemo(() => _.keyBy(apps, 'name'), [apps]);
  const countryNameByCode = useMemo(
    () => Object.fromEntries(countryOptions.map((o) => [o.value, o.label])),
    [countryOptions]
  );

  // Distinct revision values of the selected SW (newest first).
  const versionOptions = useMemo(() => {
    const app = appByName[form.appName];
    if (!app) {
      return [];
    }
    const revs = Array.from(
      new Set((app.manualRevisions || []).map((r) => String(r.revision)).filter(Boolean))
    );
    return revs
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      .map((r) => ({ value: r, label: r }));
  }, [appByName, form.appName]);

  const onSave = useCallback(async () => {
    setError('');
    if (form.countries.length === 0) {
      setError(t('Select at least one country.'));
      return;
    }
    if (!form.appName) {
      setError(t('Select a product.'));
      return;
    }
    if (form.mode === 'versions' && form.versions.length === 0) {
      setError(t('Select at least one version.'));
      return;
    }
    setSaving(true);
    try {
      await addException({
        countries: form.countries,
        appName: form.appName,
        mode: form.mode,
        versions: form.mode === 'versions' ? form.versions : [],
      });
      setForm(EMPTY_FORM);
      await refresh();
    } catch (e) {
      setError(t('Save failed'));
    } finally {
      setSaving(false);
    }
  }, [form, refresh, t]);

  const onDelete = useCallback(
    async (id) => {
      try {
        await deleteException(id);
        await refresh();
      } catch (e) {
        setError(t('Delete failed'));
      }
    },
    [refresh, t]
  );

  const refreshNotices = useCallback(() => getAllNotices().then(setNotices), []);

  const setNoticeField = useCallback((lang, field, value) => {
    setNotice((p) => ({
      ...p,
      content: {
        ...p.content,
        [lang]: { ...(p.content[lang] || { title: '', body: '' }), [field]: value },
      },
    }));
  }, []);

  const noticeHasContent = useMemo(
    () =>
      Object.values(notice.content).some(
        (c) => (c?.title || '').trim() || (c?.body || '').trim()
      ),
    [notice.content]
  );

  // Human-readable summary of when the new notice will stop showing.
  const expiryHint = useMemo(() => {
    if (expiryDays === null) {
      return t('noticeExpiry.hintIndefinite');
    }
    const d = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);
    return t('noticeExpiry.hintUntil', { date: fmtDate(d) });
  }, [expiryDays, t]);

  const onSaveNotice = useCallback(async () => {
    setNoticeError('');
    if (!noticeHasContent) {
      setNoticeError(t('criticalNoticeAdmin.needContent'));
      return;
    }
    setNoticeSaving(true);
    try {
      await addNotice({ content: notice.content, expiresInDays: expiryDays });
      setNotice(EMPTY_NOTICE);
      setExpiryDays(DEFAULT_EXPIRY_DAYS);
      await refreshNotices();
    } catch (e) {
      setNoticeError(t('Save failed'));
    } finally {
      setNoticeSaving(false);
    }
  }, [notice.content, noticeHasContent, expiryDays, refreshNotices, t]);

  const onDeleteNotice = useCallback(
    async (id) => {
      try {
        await deleteNotice(id);
        await refreshNotices();
      } catch (e) {
        setNoticeError(t('Delete failed'));
      }
    },
    [refreshNotices, t]
  );

  // Short preview of a notice (English first, else any available language).
  const noticePreview = (content) => {
    const c = content?.en || content?.[Object.keys(content || {})[0]] || {};
    return (c.title || c.body || '').trim();
  };

  return (
    <>
      <EifuHeader />
      <section className="wrapper bg-apps apps-list eifu-admin-page">
        <style>{ADMIN_STYLE}</style>
        <div
          className="container"
          style={{ paddingTop: '5.5rem', paddingBottom: '3rem', maxWidth: '1040px' }}
        >
          <div className="admin-title">{t('eIFU Admin')}</div>

          {/* Tabs: country exceptions | notices */}
          <div className="admin-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'exceptions'}
              className={tab === 'exceptions' ? 'active' : ''}
              onClick={() => setTab('exceptions')}
            >
              {t('Country exceptions')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'notices'}
              className={tab === 'notices' ? 'active' : ''}
              onClick={() => setTab('notices')}
            >
              {t('Notices')}
            </button>
          </div>

          {tab === 'exceptions' && (
          <>
          <div className="alert alert-info" role="alert">
            {t(
              'By default every manual is visible in all countries. Add an exception to hide a product or limit it to specific versions for the selected countries.'
            )}
          </div>

          {/* Add exception form */}
          <div className="admin-card">
            <div className="admin-section">{t('Add exception')}</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label mb-1">{t('Exception countries')}</label>
                <Select
                  isMulti
                  options={countryOptions}
                  value={countryOptions.filter((o) => form.countries.includes(o.value))}
                  onChange={(sel) =>
                    setForm((p) => ({ ...p, countries: (sel || []).map((s) => s.value) }))
                  }
                  placeholder={t('Select countries')}
                  classNamePrefix="ray-select"
                  menuPlacement="auto"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label mb-1">{t('Product')}</label>
                <Select
                  options={appOptions}
                  value={appOptions.find((o) => o.value === form.appName) || ''}
                  onChange={(sel) =>
                    setForm((p) => ({ ...p, appName: sel?.value || '', versions: [] }))
                  }
                  placeholder={t('Select a product')}
                  isClearable
                  classNamePrefix="ray-select"
                  menuPlacement="auto"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label mb-1 d-block">{t('Exception type')}</label>
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${form.mode === 'hide' ? 'btn-primary' : 'btn-outline-primary'}`}
                    aria-pressed={form.mode === 'hide'}
                    onClick={() => setForm((p) => ({ ...p, mode: 'hide' }))}
                  >
                    {t('Hide product entirely')}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${form.mode === 'versions' ? 'btn-primary' : 'btn-outline-primary'}`}
                    aria-pressed={form.mode === 'versions'}
                    onClick={() => setForm((p) => ({ ...p, mode: 'versions' }))}
                  >
                    {t('Show only selected versions')}
                  </button>
                </div>
              </div>
              {form.mode === 'versions' && (
                <div className="col-md-8">
                  <label className="form-label mb-1">{t('Visible versions')}</label>
                  <Select
                    isMulti
                    options={versionOptions}
                    value={versionOptions.filter((o) => form.versions.includes(o.value))}
                    onChange={(sel) =>
                      setForm((p) => ({ ...p, versions: (sel || []).map((s) => s.value) }))
                    }
                    placeholder={form.appName ? t('Select versions') : t('Select a product first')}
                    isDisabled={!form.appName}
                    classNamePrefix="ray-select"
                    menuPlacement="auto"
                  />
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-3 mt-3">
              <button type="button" className="btn btn-sm btn-primary" disabled={saving} onClick={onSave}>
                {t('Save')}
              </button>
              {error && <span className="text-danger small">{error}</span>}
            </div>
          </div>

          {/* Existing exceptions */}
          <div className="admin-section">{t('Exceptions')}</div>
          {loading && <div>{t('Checking the latest documents.')}</div>}
          {!loading && exceptions.length === 0 && (
            <div className="text-muted">{t('No exceptions — every manual is visible in all countries.')}</div>
          )}
          {!loading && exceptions.length > 0 && (
            <table className="table table-order mt-2" style={{ borderTop: '1px solid #8574FF' }}>
              <thead>
                <tr>
                  <th>{t('Product')}</th>
                  <th>{t('Exception')}</th>
                  <th style={{ minWidth: '260px' }}>{t('Countries')}</th>
                  <th style={{ width: '90px' }} />
                </tr>
              </thead>
              <tbody>
                {exceptions.map((ex) => (
                  <tr key={ex.id}>
                    <td>{appByName[ex.appName]?.title || ex.appName}</td>
                    <td>
                      {ex.mode === 'hide' ? (
                        <Badge bg="danger">{t('Hidden')}</Badge>
                      ) : (
                        <span>
                          <Badge bg="primary" className="me-1">{t('Versions')}</Badge>
                          {(ex.versions || []).join(', ')}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(ex.countries || []).map((code) => (
                          <Badge bg="secondary" key={code}>{countryNameByCode[code] || code}</Badge>
                        ))}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => onDelete(ex.id)}
                      >
                        {t('Delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </>
          )}

          {/* Critical safety notices (GH #38) */}
          {tab === 'notices' && (
          <>
          <div className="alert alert-info" role="alert">
            {t('criticalNoticeAdmin.help')}
          </div>
          <div className="admin-card">
            <div className="admin-section">{t('Add notice')}</div>
            <label className="form-label mb-1 d-block">{t('Languages')}</label>
            <div className="btn-group flex-wrap mb-3" role="group">
              {NOTICE_LANGS.map((l) => {
                const c = notice.content[l.code];
                const filled = !!((c?.title || '').trim() || (c?.body || '').trim());
                return (
                  <button
                    key={l.code}
                    type="button"
                    className={`btn btn-sm ${notice.lang === l.code ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setNotice((p) => ({ ...p, lang: l.code }))}
                  >
                    {l.label}
                    {filled ? ' •' : ''}
                  </button>
                );
              })}
            </div>
            <div className="row g-2">
              <div className="col-12">
                <label className="form-label mb-1">{t('Notice title')}</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={notice.content[notice.lang]?.title || ''}
                  onChange={(e) => setNoticeField(notice.lang, 'title', e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label mb-1">{t('Notice message')}</label>
                <textarea
                  className="form-control form-control-sm"
                  rows={4}
                  value={notice.content[notice.lang]?.body || ''}
                  onChange={(e) => setNoticeField(notice.lang, 'body', e.target.value)}
                />
              </div>
            </div>

            {/* Display period — auto-hide from the portal after N days so the
                operator need not return to remove it. null = no expiry. */}
            <label className="form-label mb-1 d-block mt-3">{t('Display period')}</label>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <div className="btn-group" role="group">
                {EXPIRY_PRESETS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`btn btn-sm ${expiryDays === d ? 'btn-primary' : 'btn-outline-primary'}`}
                    aria-pressed={expiryDays === d}
                    onClick={() => setExpiryDays(d)}
                  >
                    {t('noticeExpiry.days', { n: d })}
                  </button>
                ))}
                <button
                  type="button"
                  className={`btn btn-sm ${expiryDays === null ? 'btn-primary' : 'btn-outline-primary'}`}
                  aria-pressed={expiryDays === null}
                  onClick={() => setExpiryDays(null)}
                >
                  {t('No expiry')}
                </button>
              </div>
              <span className="text-muted">{t('or')}</span>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm expiry-days"
                value={expiryDays === null ? '' : expiryDays}
                placeholder={String(DEFAULT_EXPIRY_DAYS)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setExpiryDays(Number.isInteger(v) && v > 0 ? v : null);
                }}
              />
              <span className="text-muted">{t('days')}</span>
            </div>
            <div className="expiry-hint mt-1">{expiryHint}</div>

            <div className="d-flex align-items-center gap-3 mt-3">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={noticeSaving}
                onClick={onSaveNotice}
              >
                {t('Add notice')}
              </button>
              {noticeError && <span className="text-danger small">{noticeError}</span>}
            </div>
          </div>

          <div className="admin-section">{t('Registered notices')}</div>
          {!loading && notices.length === 0 && (
            <div className="text-muted">{t('No notices.')}</div>
          )}
          {!loading && notices.length > 0 && (
            <table className="table table-order mt-2" style={{ borderTop: '1px solid #8574FF' }}>
              <thead>
                <tr>
                  <th>{t('Notice title')}</th>
                  <th style={{ minWidth: '150px' }}>{t('Languages')}</th>
                  <th style={{ width: '90px' }}>{t('Status')}</th>
                  <th style={{ width: '130px' }}>{t('Expires')}</th>
                  <th style={{ width: '90px' }} />
                </tr>
              </thead>
              <tbody>
                {notices.map((n) => {
                  const expired = isNoticeExpired(n);
                  return (
                    <tr key={n.id} style={expired ? { opacity: 0.6 } : undefined}>
                      <td>{noticePreview(n.content)}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {Object.keys(n.content || {}).map((code) => (
                            <Badge bg="secondary" key={code}>
                              {(NOTICE_LANGS.find((l) => l.code === code) || {}).label || code}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td>
                        {expired ? (
                          <Badge bg="secondary">{t('Expired')}</Badge>
                        ) : (
                          <Badge bg="success">{t('Active')}</Badge>
                        )}
                      </td>
                      <td>
                        {n.expiresAt ? fmtDate(parseUtc(n.expiresAt)) : t('Indefinite')}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDeleteNotice(n.id)}
                        >
                          {t('Delete')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          </>
          )}
        </div>
      </section>
    </>
  );
};

export default EifuAdmin;
